import { Timestamp } from 'firebase/firestore';
import type { DocumentData, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions } from 'firebase/firestore';

const snapshotOptions: SnapshotOptions = { serverTimestamps: 'estimate' };

const convertFirestoreSession = (session: introwise.FirestoreReadData<introwise.Session>): introwise.Session => ({
  ...session,
  createdAt: session.createdAt.toDate(),
  start: session.start.toDate(),
  end: session.end.toDate(),
  ...(session.rescheduledAt && { rescheduledAt: session.rescheduledAt.toDate() }),
  ...(session.rescheduledStart && { rescheduledStart: session.rescheduledStart.toDate() }),
  ...(session.rescheduledEnd && { rescheduledEnd: session.rescheduledEnd.toDate() }),
  ...(session.cancelledAt && { cancelledAt: session.cancelledAt.toDate() }),
});

export const firestoreSessionConverter: FirestoreDataConverter<introwise.Session, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreSession({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.Session>),
    }),
  toFirestore: (val: introwise.Session) => val,
};

const convertFirestoreGroupSession = (
  session: introwise.FirestoreReadData<introwise.GroupSession>,
): introwise.GroupSession => ({
  ...session,
  createdAt: session.createdAt.toDate(),
  start: session.start.toDate(),
  end: session.end.toDate(),
  ...(session.rescheduledAt && { rescheduledAt: session.rescheduledAt.toDate() }),
  ...(session.rescheduledStart && { rescheduledStart: session.rescheduledStart.toDate() }),
  ...(session.rescheduledEnd && { rescheduledEnd: session.rescheduledEnd.toDate() }),
  ...(session.cancelledAt && { cancelledAt: session.cancelledAt.toDate() }),
});

export const firestoreGroupSessionConverter: FirestoreDataConverter<introwise.GroupSession, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreGroupSession({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.GroupSession>),
    }),
  toFirestore: (val: introwise.GroupSession) => val,
};

const convertFirestorePersonalSession = (
  session: introwise.FirestoreReadData<introwise.PersonalSession>,
): introwise.PersonalSession => ({
  ...session,
  createdAt: session.createdAt.toDate(),
  start: session.start.toDate(),
  end: session.end.toDate(),
  ...(session.rescheduledAt && { rescheduledAt: session.rescheduledAt.toDate() }),
  ...(session.rescheduledStart && { rescheduledStart: session.rescheduledStart.toDate() }),
  ...(session.rescheduledEnd && { rescheduledEnd: session.rescheduledEnd.toDate() }),
  ...(session.cancelledAt && { cancelledAt: session.cancelledAt.toDate() }),
});

export const firestorePersonalSessionConverter: FirestoreDataConverter<introwise.PersonalSession, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestorePersonalSession({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.PersonalSession>),
    }),
  toFirestore: (val: introwise.PersonalSession) => val,
};

const convertFirestorePackage = (pack: introwise.FirestoreReadData<introwise.Package>): introwise.Package => ({
  ...pack,
  createdAt: pack.createdAt.toDate(),
});

export const firestorePackageConverter: FirestoreDataConverter<introwise.Package, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestorePackage({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.Package>),
    }),
  toFirestore: (val: introwise.Package) => val,
};

const convertFirestoreBookedPackage = (
  pack: introwise.FirestoreReadData<introwise.BookedPackage>,
): introwise.BookedPackage => ({
  ...pack,
  createdAt: pack.createdAt.toDate(),
  ...(pack.expiresAt && { expiresAt: pack.expiresAt.toDate() }),
  ...(pack.cancelledAt && { cancelledAt: pack.cancelledAt.toDate() }),
});

export const firestoreBookedPackageConverter: FirestoreDataConverter<introwise.BookedPackage, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreBookedPackage({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.BookedPackage>),
    }),
  toFirestore: (val: introwise.BookedPackage) => val,
};

// Nested fields keep FirestoreReadData shapes at runtime (Timestamps, not Dates); the cast suppresses the mismatch.
// Until the INT-227 cleanup, older page docs still carry the legacy embedded services/series/packages, which the type
// leaves out: read offerings from the page's collections (usePageOfferings) instead.
const convertFirestorePage = (page: introwise.FirestoreReadData<introwise.Page>): introwise.Page =>
  ({
    ...page,
    ...(page.createdAt && { createdAt: page.createdAt.toDate() }),
  }) as unknown as introwise.Page;

export const firestorePageConverter: FirestoreDataConverter<introwise.Page, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestorePage({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.Page>),
    }),
  toFirestore: (val: introwise.Page) => val,
};

const convertConnectedAccounts = (accounts: introwise.FirestoreReadData<introwise.User>['connectedAccounts']) =>
  Object.keys(accounts).reduce(
    (acc, key) => ({ ...acc, [key]: { ...accounts[key], connectedAt: accounts[key].connectedAt.toDate() } }),
    {} as introwise.User['connectedAccounts'],
  );

const convertFirestoreUser = (user: introwise.FirestoreReadData<introwise.User>): introwise.User => ({
  ...user,
  ...(user.createdAt && { createdAt: user.createdAt.toDate() }),
  ...(user.invite && {
    invite: {
      ...user.invite,
      ...(user.invite.createdAt && { createdAt: user.invite.createdAt.toDate() }),
      ...(user.invite.expireAt && { expireAt: user.invite.expireAt.toDate() }),
      ...(user.invite.acceptedAt && { acceptedAt: user.invite.acceptedAt.toDate() }),
    },
  }),
  ...(user.connectedAccounts && {
    connectedAccounts: convertConnectedAccounts(user.connectedAccounts),
  }),
  // The owner can write any value here, so a stray one is dropped instead of breaking every reader of this user
  ...(user.navSeen && {
    navSeen: Object.fromEntries(
      Object.entries(user.navSeen).flatMap(([id, seenAt]) =>
        seenAt instanceof Timestamp ? [[id, seenAt.toDate()]] : [],
      ),
    ),
  }),
});

export const firestoreUserConverter: FirestoreDataConverter<introwise.User, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreUser({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.User>),
    }),
  toFirestore: (val: introwise.User) => val,
};

const convertFirestoreBooking = (booking: introwise.FirestoreReadData<introwise.Booking>): introwise.Booking => ({
  ...booking,
  ...(booking.createdAt && { createdAt: booking.createdAt.toDate() }),
});

export const firestoreBookingConverter: FirestoreDataConverter<introwise.Booking, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreBooking({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.Booking>),
    }),
  toFirestore: (val: introwise.Booking) => val,
};

const convertFirestorePayment = (payment: introwise.FirestoreReadData<introwise.Payment>): introwise.Payment => ({
  ...payment,
  ...(payment.createdAt && { createdAt: payment.createdAt.toDate() }),
  ...(payment.refundedAt && { refundedAt: payment.refundedAt.toDate() }),
});

export const firestorePaymentConverter: FirestoreDataConverter<introwise.Payment, DocumentData> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestorePayment({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<introwise.Payment>),
    }),
  toFirestore: (val: introwise.Payment) => val,
};

const convertFirestoreDiscountCode = (
  discountCode: introwise.FirestoreReadData<introwise.DiscountCode>,
): introwise.DiscountCode => ({
  ...discountCode,
  createdAt: discountCode.createdAt.toDate(),
  ...(discountCode.lastRedeemedAt && { lastRedeemedAt: discountCode.lastRedeemedAt.toDate() }),
});

export const firestoreDiscountCodeConverter: FirestoreDataConverter<introwise.DiscountCode> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.DiscountCode>>) =>
    convertFirestoreDiscountCode({
      id: val.id,
      ...val.data(snapshotOptions),
    }),
  toFirestore: (val: introwise.DiscountCode) => val,
};

const convertFirestoreAvailabilityPeriod = (period: introwise.FirestoreReadData<introwise.AvailabilityPeriod>) => ({
  ...period,
  start: period.start.toDate(),
  end: period.end.toDate(),
});

export const firestoreAvailabilityRuleConverter: FirestoreDataConverter<introwise.AvailabilityRules> = {
  fromFirestore: (val) => ({ id: val.id, ...(val.data() as introwise.FirestoreReadData<introwise.AvailabilityRules>) }),
  toFirestore: (val) => val,
};

export const firestoreAvailabilityPeriodConverter: FirestoreDataConverter<introwise.AvailabilityPeriod> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.AvailabilityPeriod>>) =>
    convertFirestoreAvailabilityPeriod({
      id: val.id,
      ...val.data(),
    }),
  toFirestore: (val) => val,
};

export const firestoreAvailabilityOverrideConverter: FirestoreDataConverter<introwise.AvailabilityOverride> = {
  fromFirestore: (
    val: QueryDocumentSnapshot<introwise.FirestoreReadData<Omit<introwise.AvailabilityOverride, 'id'>>>,
  ) => ({
    id: val.id,
    periods: val.data().periods.map((period) => ({ start: period.start.toDate(), end: period.end.toDate() })),
  }),
  toFirestore: (val) => val,
};

export const firestoreCallsUsageConverter: FirestoreDataConverter<introwise.CallsUsage> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.CallsUsage>>) => ({
    id: val.id,
    ...val.data(),
  }),
  toFirestore: (val) => val,
};

export const firestoreCallsUsageByMonthConverter: FirestoreDataConverter<introwise.CallsUsageByMonth> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.CallsUsageByMonth>>) => ({
    id: val.id,
    ...val.data(),
  }),
  toFirestore: (val) => val,
};

export const firestoreCallsUsageCreditsBalanceConverter: FirestoreDataConverter<introwise.CallsUsageCreditsBalance> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.CallsUsageCreditsBalance>>) => ({
    id: val.id,
    ...val.data(),
  }),
  toFirestore: (val) => val,
};

export const firestoreResourceRecordingConverter: FirestoreDataConverter<introwise.ResourceRecording> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.ResourceRecording>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data().createdAt.toDate(),
    ...(val.data().updatedAt && { updatedAt: val.data().updatedAt.toDate() }),
  }),
  toFirestore: (val) => val,
};

export const firestoreCallBackgroundConverter: FirestoreDataConverter<introwise.CallBackground> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.CallBackground>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreCallFileConverter: FirestoreDataConverter<introwise.CallFile> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.CallFile>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreClientConverter: FirestoreDataConverter<introwise.Client> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Client>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
    ...(val.data().lastActiveAt && { lastActiveAt: val.data().lastActiveAt.toDate() }),
    ...(val.data().lastSessionAt && { lastSessionAt: val.data().lastSessionAt.toDate() }),
  }),
  toFirestore: (val) => val,
};

export const firestoreNoteConverter: FirestoreDataConverter<introwise.Note> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Note>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
    ...(val.data().updatedAt && { updatedAt: val.data().updatedAt.toDate() }),
  }),
  toFirestore: (val) => val,
};

export const firestoreWorkflowConverter: FirestoreDataConverter<introwise.Workflow> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Workflow>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreWorkflowActionConverter: FirestoreDataConverter<introwise.WorkflowAction> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.WorkflowAction>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreWorkflowScheduleConverter: FirestoreDataConverter<introwise.WorkflowSchedule> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.WorkflowSchedule>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
    ...(val.data().updatedAt && { updatedAt: val.data().updatedAt.toDate() }),
    ...(val.data().lastTriggerAt && { lastTriggerAt: val.data().lastTriggerAt.toDate() }),
    executions: Object.fromEntries(
      Object.entries(val.data().executions).map(([key, value]) => [
        key,
        {
          ...value,
          executionAt: value.executionAt.toDate(),
        },
      ]),
    ),
  }),
  toFirestore: (val) => val,
};

const convertPaymentPlanPayment = (
  payment: introwise.FirestoreReadData<introwise.PaymentPlanPayment>,
): introwise.PaymentPlanPayment => ({
  ...payment,
  createdAt: payment.createdAt.toDate(),
  dueAt: payment.dueAt.toDate(),
  ...(payment.nextAttemptAt && { nextAttemptAt: payment.nextAttemptAt.toDate() }),
  ...(payment.processedAt && { processedAt: payment.processedAt.toDate() }),
});

export const firestorePaymentPlanConverter: FirestoreDataConverter<introwise.PaymentPlan> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.PaymentPlan>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
    ...(val.data().completedAt && { completedAt: val.data().completedAt.toDate() }),
    ...(val.data().cancelledAt && { cancelledAt: val.data().cancelledAt.toDate() }),
    payments: Object.fromEntries(
      Object.entries(val.data().payments).map(([key, value]) => [key, convertPaymentPlanPayment(value)]),
    ),
  }),
  toFirestore: (val) => val,
};

export const firestoreTagConverter: FirestoreDataConverter<introwise.Tag> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Tag>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreLocationConverter: FirestoreDataConverter<introwise.Location> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Location>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreFormDefinitionConverter: FirestoreDataConverter<introwise.FormDefinition> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.FormDefinition>>) => {
    const data = val.data(snapshotOptions);
    return {
      id: val.id,
      ...data,
      createdAt: data.createdAt.toDate(),
      updatedAt: data.updatedAt.toDate(),
      ...(data.archivedAt && { archivedAt: data.archivedAt.toDate() }),
    };
  },
  toFirestore: (val) => val,
};

export const firestoreFormResponseConverter: FirestoreDataConverter<introwise.FormResponse> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.FormResponse>>) => {
    const data = val.data(snapshotOptions);
    return {
      id: val.id,
      ...data,
      submittedAt: data.submittedAt.toDate(),
    };
  },
  toFirestore: (val) => val,
};

export const firestoreServiceConverter: FirestoreDataConverter<introwise.Service> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Service>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

export const firestoreSeriesConverter: FirestoreDataConverter<introwise.Series> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.Series>>) => ({
    id: val.id,
    ...val.data(),
    createdAt: val.data({ serverTimestamps: 'estimate' }).createdAt.toDate(),
  }),
  toFirestore: (val) => val,
};

const convertFirestoreDigitalProduct = (
  product: introwise.FirestoreReadData<introwise.DigitalProduct>,
): introwise.DigitalProduct => ({
  ...product,
  createdAt: product.createdAt.toDate(),
  ...(product.updatedAt && { updatedAt: product.updatedAt.toDate() }),
});

export const firestoreDigitalProductConverter: FirestoreDataConverter<introwise.DigitalProduct> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.DigitalProduct>>) =>
    convertFirestoreDigitalProduct({
      id: val.id,
      ...val.data(snapshotOptions),
    }),
  toFirestore: (val) => val,
};

const convertFirestoreContentLibraryItem = (
  item: introwise.FirestoreReadData<introwise.ContentLibraryFile | introwise.ContentLibraryUrl>,
) =>
  ({
    ...item,
    createdAt: item.createdAt.toDate(),
    ...(item.updatedAt && { updatedAt: item.updatedAt.toDate() }),
    ...(item.deletedAt && { deletedAt: item.deletedAt.toDate() }),
  }) as introwise.ContentLibraryFile | introwise.ContentLibraryUrl;

export const firestoreContentLibraryItemConverter: FirestoreDataConverter<
  introwise.ContentLibraryFile | introwise.ContentLibraryUrl
> = {
  fromFirestore: (val: QueryDocumentSnapshot) =>
    convertFirestoreContentLibraryItem({
      id: val.id,
      ...(val.data(snapshotOptions) as introwise.FirestoreReadData<
        introwise.ContentLibraryFile | introwise.ContentLibraryUrl
      >),
    }),
  toFirestore: (val) => val,
};

const convertFirestoreDigitalProductPurchase = (
  purchase: introwise.FirestoreReadData<introwise.DigitalProductPurchase>,
): introwise.DigitalProductPurchase => ({
  ...purchase,
  createdAt: purchase.createdAt.toDate(),
  ...(purchase.lastAccessedAt && { lastAccessedAt: purchase.lastAccessedAt.toDate() }),
  content: purchase.content.map((item) => ({
    ...item,
    ...(item.deletedAt && { deletedAt: item.deletedAt.toDate() }),
    ...(item.updatedAt && { updatedAt: item.updatedAt.toDate() }),
  })),
});

export const firestoreDigitalProductPurchaseConverter: FirestoreDataConverter<introwise.DigitalProductPurchase> = {
  fromFirestore: (val: QueryDocumentSnapshot<introwise.FirestoreReadData<introwise.DigitalProductPurchase>>) =>
    convertFirestoreDigitalProductPurchase({
      id: val.id,
      ...val.data(snapshotOptions),
    }),
  toFirestore: (val) => val,
};
